import './Lesson.scss'
import '../../css/plyr.styles.scss'

import { useEffect, useState, useCallback, useContext, useMemo } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { Button, Col, Container, Row } from 'react-bootstrap'
import parse from 'html-react-parser'

import Error from '../common/error/Error'
import { is404 } from '../common/api'
import { navigateTo } from 'components/common/navigation'
import {
  getFullCourseById,
  getLessonById,
  getLessonCacheKey,
  getLessonTopics,
  getTopicCacheKey,
  isFreeCourse,
  isFreeTopic,
  LMS_TYPE_PREFIX,
  tryGetCourseField,
  tryGetTopicDataForLessonFromCourseSteps
} from '../common/api/Courses'
import { getQuizzesForCourseLessonTopic } from '../common/api/Quizzes'
import {
  getCourseProgress,
  tryUpdateCourseProgressForCourseObject,
  isComplete,
  ACTION_COMPLETE
} from '../common/api/CourseProgress'
import {
  EVENT_CATEGORY_LESSON,
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_TOPIC,
  saveUserEventDebounced,
  EVENT_ACTION_COMPLETE
} from '../common/api/UserEvents'
import QuizList from './../quizzes/QuizList'
import LessonVideo from './LessonVideo'
import { getCoursePath } from '../common/helpers/Paths'
import CourseContext from 'components/common/contexts/CourseContext'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import Membership from 'components/account/Membership'
import Confirmation from 'components/common/messaging/Confirmation'
import { getAuthenticatedUser } from 'components/auth'
import { closingStatements } from '../common/api/constants'
import Loading from '../../templates/Loading'

/**
 * Lesson component. Used for displaying a Course Lesson along with its associated topics.
 */
export default function Lesson({ cache, setReturnPath, setHeaderTitle }) {
  const history = useHistory()
  const params = useParams()
  const { userSubscription: subscription, userHasPremiumAccess } =
    useContext(SubscriptionContext)
  const { courses } = useContext(CourseContext)
  const user = getAuthenticatedUser()

  // Did we pass a completion status in the URL?
  const { search } = useLocation()
  const searchQuery = new URLSearchParams(search)
  const isCompleted = searchQuery.get('completed') === 'true'

  const [course, setCourse] = useState()
  const [fullCourse, setFullCourse] = useState()
  const [lesson, setLesson] = useState()
  const [video, setVideo] = useState()
  const [topics, setTopics] = useState([])
  const [sessions, setSessions] = useState([])
  const [courseProgress, setCourseProgress] = useState([])
  const [quizzes, setQuizzes] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [isLocked, setIsLocked] = useState(true)
  const [isShowingTopic, setIsShowingTopic] = useState(false)
  const [lessonIsCompleted, setLessonIsCompleted] = useState(isCompleted)
  const [error, setError] = useState()

  // Use negative index as default to indicate we haven't selected a topic yet
  const [activeSection, setActiveSection] = useState(-1)
  const [displayedContent, setDisplayedContent] = useState()

  const { courseId, lessonId, topicId } = params
  const courseLength = course && course?.acf_extended?.course_length

  const checkSearch =
    search.split('=')[0] === '?autoplay' && search.split('=')[1] === 'true'

  const topicsList = topics => (checkSearch ? sessions : topics)

  // Don't use `Array.at` as it's not supported in older browsers
  const isLastLesson = () => {
    let isLast = false

    try {
      isLast =
        fullCourse?.lessons[fullCourse?.lessons?.length - 1].id ===
        parseInt(lessonId)
    } catch (e) {
      console.error(e)
    }

    return isLast
  }

  const getConfirmationMessage = (completed = false) => {
    const userWithComma = user?.display_name ? `, ${user.display_name}` : ''

    const text = completed
      ? ' on finishing all of the Sessions'
      : ` on completing "${lesson && parse(lesson?.clean_title)}"`

    return `Congratulations${text}${userWithComma}.`
  }

  // for 1 day journey or on finishing the last journey
  let confirmationMessage = getConfirmationMessage(true)

  if (courseLength >= 2 && !isLastLesson()) {
    confirmationMessage = getConfirmationMessage()
  }

  /**
   * Checks to see whether the passed in content item is the currently displayed content item
   * @param {Object} content The content to check
   */
  const _isDisplayed = useCallback(
    content => content === displayedContent,
    [displayedContent]
  )

  /**
   * Returns a boolean indictating whether the current topic is the last topic in the lesson
   * @returns Boolean indicating whether the current topic is the last topic in the lesson
   */
  const _isLastTopic = () =>
    topicsList(topics).length && activeSection === topicsList(topics).length - 1

  /**
   * Returns a boolean indictating whether the current topic is the first topic in the lesson
   * @returns Boolean indicating whether the current topic is the first topic in the lesson
   */
  const _isFirstTopic = () => activeSection === 0

  /**
   * Event handler for lesson completion, marks the currently displayed content as "completed"
   */
  const handleComplete = async () => {
    // Use our course progress helper to mark this lesson or topic as complete
    let success = await tryUpdateCourseProgressForCourseObject(
      courseId,
      displayedContent,
      ACTION_COMPLETE
    )

    // Drop the LearnDash `sfwd-` prefix and the trailing "s" if there is one
    const category = displayedContent.type
      .replace(LMS_TYPE_PREFIX, '')
      .replace(/s+$/, '')

    // Log completion
    await saveUserEventDebounced(category, ACTION_COMPLETE, displayedContent.id)

    // If this is the last topic in the lesson, mark the entire lesson as complete
    if (success && _isLastTopic()) {
      // Scroll to top of page
      window.scrollTo(0, 0)

      success = await tryUpdateCourseProgressForCourseObject(
        courseId,
        lesson,
        ACTION_COMPLETE
      )
      setLessonIsCompleted(!!success)

      // Log the event
      saveUserEventDebounced(
        EVENT_CATEGORY_LESSON,
        EVENT_ACTION_COMPLETE,
        lesson.id
      )
    }
  }

  /**
   * Event handler for "back" button
   */
  const handleBackButton = event => {
    // Scroll to top of page
    window.scrollTo(0, 0)

    let nextSection = activeSection - 1

    // Handle the case where we're on the first topic and the user clicks the back button
    if (nextSection < 0) nextSection = 0

    // Update the active section
    setActiveSection(nextSection)
  }

  /**
   * Event handler for "next" button
   */
  const handleNextButton = event => {
    // Scroll to top of page
    window.scrollTo(0, 0)

    let nextSection = activeSection + 1

    // Handle the case where we're on the last topic and the user clicks the next button
    if (nextSection > topicsList(topics).length)
      nextSection = topicsList(topics).length - 1

    // Update completed status
    if (isShowingTopic) handleComplete()

    // Update the active section
    setActiveSection(nextSection)
  }

  // Get and Set quizzes if we have them for the active topic
  const getSetQuizzesForTopic = useCallback(
    async topicId => {
      const quizResult = await getQuizzesForCourseLessonTopic(
        courseId,
        lessonId,
        topicId
      )

      // Did we get a successful result or a 404?
      if (!is404(quizResult) && quizResult.length) setQuizzes(quizResult)
    },
    [courseId, lessonId]
  )

  // Main Lesson hook
  useEffect(() => {
    // Return early if we don't have any course data yet
    if (!courses) return

    // Scroll to top
    window.scrollTo(0, 0)

    // Retrieve the cache keys
    const lessonCacheKey = getLessonCacheKey(lessonId)
    const topicCacheKey = getTopicCacheKey(lessonId, topicId)

    async function getLesson() {
      // Don't retrieve lesson or topic data if we  we don't know the course info yet or
      // if already have the lesson data
      if (!course || !fullCourse || lesson) return

      // Check cache first
      let lessonResult = cache.getCache(lessonCacheKey)
      let topicResult = cache.getCache(topicCacheKey)
      let indexOfTopic = -1

      // Cache result is empty, so let's cache it for future use
      if (!lessonResult) {
        // Make sure we can connect to the server
        try {
          lessonResult = await getLessonById(lessonId)

          // Update cache
          cache.cache(lessonCacheKey, lessonResult)
        } catch (e) {
          setError(e.message)
        }
      }

      if (!topicResult) {
        // Make sure we can connect to the server
        try {
          topicResult = await getLessonTopics(lessonId, courseId)

          // Update cache
          cache.cache(topicCacheKey, topicResult)
        } catch (e) {
          setError(e.message)
        }
      }

      // Order topics by menu order: console.table(topicResult)
      if (topicResult) topicResult.sort((a, b) => a.menu_order - b.menu_order)

      // Finds index or returns -1, indicating that no element passed the test
      try {
        indexOfTopic = topicsList(topicResult).findIndex(
          topic => topic.id === parseInt(topicId)
        )
      } catch (e) {} // Swallow exceptions

      setVideo(lessonResult)
      setLesson(lessonResult)
      setTopics(topicResult)
      setActiveSection(indexOfTopic)

      // Don't disable loading if we're still loading a topic
      if (indexOfTopic < 0) {
        // Does the Lesson itself have quizzes? If so, let's show them
        getSetQuizzesForTopic(lessonId)

        setDisplayedContent(lessonResult)
        setIsLoading(false)
      }
    }

    // Query full course details
    async function getFullCourseAsync() {
      const cacheKey = `course_${courseId}`

      // Return if we don't have an id or courses or if we've already populated the course object
      if (!courseId || !courses.length || course) return

      // Get user's progress: console.table(progress)
      const progress = await getCourseProgress(courseId)

      // Get our course
      const courseMatch = courses.find(
        course => course.id === parseInt(courseId)
      )
      const coursePath = getCoursePath(courseId)
      const headerTitle = courseMatch?.title?.rendered
        ? parse(courseMatch.title.rendered)
        : ''

      // Check cache first
      const courseResult = await cache.tryGetAsyncResult(
        cacheKey,
        getFullCourseById,
        this,
        courseId
      )

      // Set our title and return path
      setHeaderTitle(headerTitle)
      setReturnPath(coursePath)

      // Set Course details
      setFullCourse(courseResult)
      setCourseProgress(progress)
      setCourse(courseMatch)
    }

    getFullCourseAsync()
    getLesson()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courses,
    fullCourse,
    lesson,
    courseId,
    lessonId,
    topicId,
    getSetQuizzesForTopic,
    cache,
    setReturnPath,
    setHeaderTitle,
    course
  ])

  // Topic detail hook
  useEffect(() => {
    // Return early if we're still loading
    if (!course || !subscription || !lesson || !courseProgress) return

    // Scroll to top
    window.scrollTo(0, 0)

    // Do we have topics loaded and a valid section selection?
    const shouldShowTopic =
      activeSection >= 0 &&
      topicsList(topics)?.length &&
      topicsList(topics)[activeSection]

    // Did the user select a topic to display? If not, reset to the lesson if it's not already visible
    if (!shouldShowTopic && !_isDisplayed(lesson)) {
      // Log the event
      saveUserEventDebounced(
        EVENT_CATEGORY_LESSON,
        EVENT_ACTION_PAGEVIEW,
        lesson.id
      )

      setIsLoading(false)
      setVideo(lesson)
      return setDisplayedContent(lesson)
    }

    // The selected topic
    const topic = topicsList(topics)[activeSection]

    // Return early if we passed an invalid index somehow or if the topic is already visible
    if (!topic || _isDisplayed(topic)) return

    // Does the user have access to this topic?
    const locked =
      !userHasPremiumAccess && !isFreeCourse(course) && !isFreeTopic(topic)

    setIsLocked(locked)

    // Don't update course items that have already been completed
    // Update course progress, but don't wait for a response and
    if (!isComplete(courseProgress, topic))
      tryUpdateCourseProgressForCourseObject(course.id, topic)

    // Log topic access
    saveUserEventDebounced(
      EVENT_CATEGORY_TOPIC,
      EVENT_ACTION_PAGEVIEW,
      topic.id
    )

    // Does the active section have quizzes? If so, let's show them
    getSetQuizzesForTopic(topic.id)
    setIsLoading(false)
    setVideo(topic)
    setDisplayedContent(topic)
    setIsShowingTopic(!!topic)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscription,
    userHasPremiumAccess,
    course,
    lesson,
    topics,
    isLoading,
    activeSection,
    courseProgress,
    _isDisplayed,
    getSetQuizzesForTopic
  ])

  useMemo(() => {
    let sessionsList = []

    fullCourse &&
      fullCourse.sections &&
      fullCourse.sections.forEach(
        section =>
          section.lessons &&
          section.lessons.forEach(lesson => {
            // Get topic object from steps data
            let topics = tryGetTopicDataForLessonFromCourseSteps(
              lesson.id,
              fullCourse.steps
            )

            // Convert topic object to array and merge with api result
            topics = Object.keys(topics).map(topicId =>
              fullCourse.topics.find(topic => topic.id === parseInt(topicId))
            )

            topics.forEach(topic => {
              const videoDuration = tryGetCourseField(topic, 'video_duration')

              videoDuration !== '' && topic && sessionsList.push(topic)
            })
          })
      )

    setSessions(sessionsList)
  }, [fullCourse])

  return (
    <>
      <Error error={error} />

      <Container
        fluid
        onMouseDown={() => false} // Prevent copying text
        onContextMenu={() => false} // Prevent copying text
        className={`lesson-container course-${courseId} lesson-${lessonId} topic-${topicId} ${
          lessonIsCompleted ? 'lesson-completed' : ''
        }`}
      >
        <Loading
          isLoaded={isLoading || !displayedContent}
          loadingClass='h-100 py-5'
        >
          {isLocked ? (
            <div className='lesson-content-wrapper'>
              <h1 className='lesson-title'>
                {displayedContent?.clean_title &&
                  parse(displayedContent.clean_title)}
              </h1>
              <h2 className='text-center mt-4'>
                Access to this lesson is locked and requires a subscription
              </h2>
              <Membership />
            </div>
          ) : (
            <>
              {/* If we marked the lesson as complete, let's show the completion */}
              {lessonIsCompleted ? (
                <div className='lesson-complete-summary'>
                  {/* Show confirmation message */}
                  <Confirmation
                    message={confirmationMessage}
                    fullCourse={fullCourse}
                    courseProgress={courseProgress}
                    closingStatement={
                      closingStatements[
                        Math.floor(Math.random() * closingStatements.length)
                      ]
                    }
                    btnText={`Return to Course page`}
                    btnOnClick={() =>
                      navigateTo(getCoursePath(courseId), history)
                    }
                    showConfetti={lessonIsCompleted}
                    lesson={lesson}
                  />
                </div>
              ) : (
                <>
                  <Row className='lesson-navigation-wrapper' xs={2}>
                    <Col>
                      {!_isFirstTopic() && (
                        <span
                          className='lesson-navigation-link'
                          onClick={handleBackButton}
                        >
                          <i className='bi bi-arrow-left' /> Back
                        </span>
                      )}
                    </Col>
                    <Col className='text-right'>
                      {!_isLastTopic() && (
                        <span
                          className='lesson-navigation-link'
                          onClick={handleNextButton}
                        >
                          Next <i className='bi bi-arrow-right' />
                        </span>
                      )}
                    </Col>
                  </Row>
                  <LessonVideo
                    video={{
                      ...video,
                      course_name: course && course.title.rendered
                    }}
                    key={displayedContent && displayedContent.id}
                    handleNextButton={handleNextButton}
                    autoplay={checkSearch}
                    practiceType={displayedContent.type}
                  />
                  <div className='lesson-content-wrapper'>
                    <h1 className='lesson-title'>
                      {displayedContent?.clean_title &&
                        parse(displayedContent.clean_title)}
                    </h1>
                    <div className='lesson-content'>
                      {displayedContent?.content?.rendered &&
                        parse(displayedContent.content.rendered)}
                    </div>
                    <QuizList
                      courseId={courseId}
                      topic={displayedContent}
                      lesson={lesson}
                      quizzes={quizzes}
                    />
                    <div className='lesson-actions'>
                      {_isLastTopic() && (
                        <Button
                          variant='primary'
                          size='lg'
                          className='lesson-completion-link'
                          onClick={handleComplete}
                          disabled={isLoading || lessonIsCompleted}
                        >
                          Mark Session as Complete
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Loading>
      </Container>
    </>
  )
}
